import { rememberContentReturn } from './content-return'
import { useAuth0 } from '@auth0/auth0-react'
import {
	IconButton, Tooltip, useEnterExit, useAnchoredPlacement,
	anchoredLeft, anchoredTop, type AnchorStrategy
} from '@clickguard/design-system'
import Announcement02 from '@untitled-ui/icons-react/build/esm/Announcement02'
import HelpCircle from '@untitled-ui/icons-react/build/esm/HelpCircle'
import Stars02 from '@untitled-ui/icons-react/build/esm/Stars02'
import XClose from '@untitled-ui/icons-react/build/esm/XClose'
import { Auth0Config, ContentAdminConfig } from 'config'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'
import { useUserStore } from 'store'
import { CoachmarkTextButton } from 'pages/account-overview/account-overview-coachmark/components/coachmark-text-button'
import { isAccountOverviewDashboardPath } from 'pages/account-overview/account-overview-dashboard'
import {
	ContentAnnouncement,
	getContentAnnouncement,
	saveContentAnnouncementSeen,
	subscribeContentAnnouncementSeen
} from './content-announcements'

/** Seen receipts belong to the checked platform identity, not browser storage. */
export const ContentUpdates = ({
	accountOverviewVisible = false,
	accountOverviewLoading = false
}: {
	accountOverviewVisible?: boolean
	accountOverviewLoading?: boolean
}) => {
	const { getAccessTokenSilently, user: authUser } = useAuth0()
	const subject = authUser?.sub
	const activeSubject = useRef(subject)
	activeSubject.current = subject
	useEffect(() => {
		activeSubject.current = subject
		// Same-tab reader navigation unmounts this navbar; it owns receipts thereafter.
		return () => { activeSubject.current = undefined }
	}, [subject])
	const { pathname, search = '', hash = '' } = useLocation()
	useEffect(() => {
		rememberContentReturn(subject, pathname + search + hash)
	}, [subject, pathname, search, hash])
	const navigate = useNavigate()
	const overviewSeen = useUserStore(
		state => state.user?.account_overview_intro_seen ?? false
	)
	const [release, setRelease] = useState<ContentAnnouncement | null>(null)
	const [dismissed, setDismissed] = useState(false)
	const [saving, setSaving] = useState(false)
	const [error, setError] = useState('')
	const pending = useRef(false)
	const selection = useRef<Promise<ContentAnnouncement | null> | null>(null)
	const seen = useRef(new Set<string>())
	const loadedSubject = useRef(subject)
	const getToken = useCallback(
		() => getAccessTokenSilently({
			audience: Auth0Config.audience,
			scope: Auth0Config.scope
		}),
		[getAccessTokenSilently]
	)
	useEffect(() => {
		seen.current.clear()
		setRelease(null)
		setDismissed(false)
		setSaving(false)
		setError('')
		pending.current = false
		selection.current = null
		if (!ContentAdminConfig.url) return
		let stopped = false
		selection.current = getContentAnnouncement(getToken)
		selection.current.then(row => {
			if (!stopped) {
				loadedSubject.current = subject
				setRelease(row)
				setDismissed(!!row && seen.current.has(row.id))
			}
		}).catch(() => {})
		return () => { stopped = true }
	}, [getToken, subject])
	useEffect(() => subscribeContentAnnouncementSeen(subject, id => {
		seen.current.add(id)
		if (id === release?.id) setDismissed(true)
	}), [release?.id, subject])
	// Hold while eligibility loads, so a fast CMS response cannot flash both cards.
	const overviewShowing = !overviewSeen && isAccountOverviewDashboardPath(pathname) &&
		(accountOverviewVisible || accountOverviewLoading)
	const { mounted, entered } = useEnterExit(
		!!release && loadedSubject.current === subject && !dismissed && !overviewShowing,
		{ duration: 180 }
	)
	const panelMounted = mounted && !!release && loadedSubject.current === subject
	const anchor = useRef<HTMLDivElement>(null)
	const panel = useRef<HTMLDivElement>(null)
	const place = useCallback((strategy: AnchorStrategy) => {
		if (!anchor.current || !panel.current) return
		const rect = anchor.current.getBoundingClientRect()
		const viewport = document.documentElement.clientWidth || window.innerWidth
		const width = Math.min(300, viewport - 32)
		const center = rect.left + rect.width / 2
		const left = Math.max(16, Math.min(center - width / 2, viewport - width - 16))
		panel.current.style.left = `${anchoredLeft(left, strategy)}px`
		panel.current.style.top = `${anchoredTop(rect.bottom + 10, strategy)}px`
		panel.current.style.setProperty(
			'--coachmark-caret-left',
			`${Math.max(12, Math.min(center - left, width - 12))}px`
		)
	}, [])
	// DS owns sticky/scroll strategy, resize observation and exit-time anchoring.
	const placement = useAnchoredPlacement({
		active: panelMounted,
		anchorRef: anchor,
		panelRef: panel,
		place
	})
	const dismiss = async (path?: string) => {
		// Open synchronously during the gesture, even while a receipt is being saved.
		if (path) window.open(path, '_blank', 'noopener,noreferrer')
		if (pending.current || dismissed) return
		const receiptSubject = subject
		pending.current = true
		setSaving(true)
		setError('')
		try {
			const row = release || await selection.current
			if (row && receiptSubject === activeSubject.current) {
				const currentToken = async () => {
					const token = await getToken()
					if (receiptSubject !== activeSubject.current) throw new Error('The signed-in user changed.')
					return token
				}
				await saveContentAnnouncementSeen(currentToken, row.id, receiptSubject)
				if (receiptSubject === activeSubject.current) setDismissed(true)
			}
		} catch {
			if (receiptSubject === activeSubject.current) setError('The seen receipt could not be saved. Try again.')
		} finally {
			if (receiptSubject === activeSubject.current) {
				pending.current = false
				setSaving(false)
			}
		}
	}
	if (!ContentAdminConfig.url) return null
	return (
		<>
			<Tooltip content="Help" position="bottom">
				<IconButton
					aria-label="Help"
					size="lg"
					iconSize={24}
					color="secondary"
					style={{ padding: 6, borderRadius: 8 }}
					className="hover:!bg-gray-100"
					icon={<HelpCircle width={24} height={24} />}
					onClick={() => navigate('/docs')}
				/>
			</Tooltip>
			<div ref={anchor} className="relative flex items-center">
				<Tooltip content="Changelog" position="bottom">
					<IconButton
						aria-label="Changelog"
						size="lg"
						iconSize={24}
						color="secondary"
						style={{ padding: 6, borderRadius: 8 }}
						className="hover:!bg-gray-100"
						icon={<Announcement02 width={24} height={24} />}
						onClick={() => {
							navigate('/docs/changelog')
							void dismiss()
						}}
					/>
				</Tooltip>
				{panelMounted && release && createPortal(
					<div
						ref={panel}
						style={{ position: placement }}
						role="dialog"
						aria-label="New in the Changelog"
						aria-busy={saving}
						hidden={overviewShowing}
						className={`z-50 w-[300px] max-w-[calc(100vw-32px)] rounded-xl bg-[var(--coachmark-surface)] p-4 text-white shadow-lg transition-[opacity,transform] duration-[180ms] ease-out motion-reduce:transition-none ${
							entered ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-1'
						}`}
					>
						<div style={{ left: 'var(--coachmark-caret-left)' }} className="absolute -top-1.5 h-3 w-3 -translate-x-1/2 rotate-45 bg-[var(--coachmark-surface)]" />
						<IconButton
							aria-label="Close"
							onClick={() => dismiss()}
							disabled={saving}
							icon={<XClose className="h-4 w-4" />}
							size="sm"
							color="white"
							className="absolute right-1.5 top-1.5"
						/>
						<div className="flex items-center gap-2 pr-6">
							<span className="text-[12px] font-semibold leading-[16px]">
								{release.kind === 'monthly' ? release.title.replace(/^✨\s*/, '') : 'Just shipped'}
							</span>
							<span className="inline-flex items-center gap-[3px] rounded-[4px] border border-[rgba(133,107,255,0.3)] bg-[rgba(133,107,255,0.1)] px-[6px] py-0 text-[10px] font-medium leading-[16px] text-[#b7b2ff]">
								<Stars02 className="h-2.5 w-2.5" />
								New
							</span>
						</div>
						<p className="mt-2 text-[12px] leading-4 text-[#ededed]">
							{release.kind === 'monthly' ? release.summary : `${release.title}. ${release.summary}`}
						</p>
						{error && <p role="alert" className="mt-2 text-xs">{error}</p>}
						<div className="mt-3 flex items-center gap-4">
							<CoachmarkTextButton
								label="Read the update"
								tone="primary"
								disabled={saving}
								onClick={() => dismiss(release.path)}
							/>
							<CoachmarkTextButton
								label="Got it"
								tone="secondary"
								disabled={saving}
								onClick={() => dismiss()}
							/>
						</div>
					</div>,
					document.body
				)}
			</div>
		</>
	)
}
